import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ProductCard } from "@/components/ProductCard";

const search = z.object({ category: z.string().optional() });

const titleCase = (s: string) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const Route = createFileRoute("/shop")({
  validateSearch: search,
  head: ({ match }) => {
    const category = (match.search as { category?: string }).category;
    const label = category ? titleCase(category) : null;
    const title = label
      ? `${label} — Buy ${label} Online | Jul's Skin`
      : "Shop Skincare & Hair Care — Jul's Skin";
    const description = label
      ? `Shop ${label.toLowerCase()} from Jul's Skin — natural, small-batch formulas with fast delivery across India.`
      : "Browse the full Jul's Skin collection — natural skincare and hair care products curated for everyday routines.";
    const url = category
      ? `https://julsskin.shop/shop?category=${encodeURIComponent(category)}`
      : "https://julsskin.shop/shop";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            isPartOf: { "@type": "WebSite", name: "Jul's Skin", url: "https://julsskin.shop" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://julsskin.shop/" },
              { "@type": "ListItem", position: 2, name: label ?? "Shop", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: Shop,
});


function Shop() {
  const { category } = Route.useSearch();
  const { data, isLoading } = useQuery({
    queryKey: ["products", category ?? "all"],
    queryFn: async () => {
      let q = supabase.from("products").select("*").order("sort_order", { ascending: true }).order("created_at", { ascending: false });
      if (category) q = q.eq("category", category);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <div className="mx-auto max-w-7xl w-full px-6 py-16">
        <p className="eyebrow text-muted-foreground">{category ?? "All"}</p>
        <h1 className="display text-4xl md:text-5xl mt-2">The Collection</h1>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {isLoading ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-muted animate-pulse" />)
            : data?.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
      <div className="flex-1" />
      <SiteFooter />
    </div>
  );
}
